import type { PatientInvoiceHistoryItem } from '@store/generated/billing-v2';

import { invoiceDeclineOf } from '../components/balance-table/rows';

/**
 * The invoice a card charge should settle, instead of raising a second
 * charge against a balance that is already invoiced (gdn-3154).
 *
 * One object rather than a handful of optional fields, because the loose
 * version failed three times in review: a call site passed two of four
 * fields, a forwarding layer read four but fell back for one, and a
 * stale closure fed the derivation a short list. Each was the same
 * shape — the type allowed a subset to arrive, so a subset did.
 *
 * Note there is no `payOpenInvoiceWithCard` flag. Presence IS the
 * instruction, so "charge the invoice, amount unknown" cannot be
 * expressed at all; before, it was one forgotten prop away.
 */
export interface OpenInvoiceTarget {
  /** The invoice to charge. */
  invoiceId: string;
  /**
   * Its own balance — the sum that will actually be taken.
   *
   * Not the selected charge lines, which total something else the moment
   * anything has been paid. The payload exposes no `amount_remaining`,
   * so a partially-paid invoice is not representable; if the BE ever
   * allows one, this is the field that must start reading the remainder.
   */
  amountCents: number;
  /**
   * How many invoices are open in total.
   *
   * Greater than one means this charge settles the oldest and leaves the
   * rest standing, which the UI has to say rather than imply the balance
   * is cleared.
   */
  openCount: number;
  /** This invoice's last decline, when it has one. */
  decline?: { failedDate: string; reason: string | null };
}

/**
 * @param openInvoices every open invoice on the patient, oldest first.
 * @param everythingIsInvoiced true when no owed cent is still uninvoiced
 *   — the only condition under which charging an existing invoice
 *   collects what the biller means to collect.
 * @returns the invoice to charge, or `null` for a considered "do not
 *   target one". Callers pass the result straight through: `null` is an
 *   answer, and consumers distinguish it from `undefined` ("nobody
 *   worked it out, derive it from the ledger").
 *
 * When several invoices are open the oldest is targeted. Refusing to
 * target anything there would send the charge back down the
 * raise-a-new-invoice path — a third invoice for the patient who already
 * has two, which is the failure this exists to stop.
 */
export const openInvoiceTargeting = (
  openInvoices: PatientInvoiceHistoryItem[],
  everythingIsInvoiced: boolean,
): OpenInvoiceTarget | null => {
  const target = openInvoices[0];
  if (!target || !everythingIsInvoiced) return null;

  const decline = invoiceDeclineOf(target);
  return {
    invoiceId: target.invoice_id,
    amountCents: target.amount_cents,
    openCount: openInvoices.length,
    decline: decline
      ? { failedDate: decline.failedDate, reason: decline.failureReason }
      : undefined,
  };
};
